import type { ActionKey } from './action.js';

/**
 * Single-Observer IS-MCTS tree node. Per Cowling/Powley/Whitehouse 2012:
 *
 * - `visits` is incremented every time this node is *selected* (descended through).
 * - `availabilityCount` per edge tracks how many iterations the edge was
 *   even *legal* given the current determinization — UCB1 uses it instead of
 *   `parent.visits` since each edge may not be available in every world.
 * - `totalReward` accumulates reward observed at terminal from the root AI's
 *   perspective (positive = good for root).
 *
 * Children are lazy: an edge exists in `edges` as soon as it has been
 * expanded once. Untried-but-legal actions for the current determinization
 * are computed on demand.
 */
export interface Edge {
  /** Engine seat that will act AFTER this edge is taken (i.e. the child's actor). */
  child: Node | null;
  visits: number;
  totalReward: number;
  /** Iterations during which this edge was legal under the determinization. */
  availabilityCount: number;
}

export class Node {
  readonly edges = new Map<ActionKey, Edge>();
  /** Seat to act at this node (engine seat index). */
  readonly actor: number;
  /** Edges contributing to UCT selection. */
  visits = 0;

  constructor(actor: number) {
    this.actor = actor;
  }

  getOrCreateEdge(key: ActionKey): Edge {
    let edge = this.edges.get(key);
    if (!edge) {
      edge = { child: null, visits: 0, totalReward: 0, availabilityCount: 0 };
      this.edges.set(key, edge);
    }
    return edge;
  }

  /**
   * UCB1 with availability count (per Cowling 2012). Returns null for
   * unvisited legal actions (caller should expand one of them first).
   *
   * `signFromRoot`: +1 at root-team nodes, −1 at opposing-team nodes. Reward
   * is stored from root's perspective; opponents pick *against* the root,
   * so their UCT must flip the exploitation sign. (Closer to MO-IS-MCTS
   * than pure SO-IS-MCTS — gives meaningfully stronger play in self-play.)
   */
  pickUct(legal: readonly ActionKey[], c: number, signFromRoot: 1 | -1): ActionKey | null {
    if (legal.length === 0) return null;
    // First: any untried legal action gets priority.
    const untried: ActionKey[] = [];
    for (const k of legal) {
      const e = this.edges.get(k);
      if (!e || e.visits === 0) untried.push(k);
    }
    if (untried.length > 0) return null;
    let best: ActionKey | null = null;
    let bestScore = -Infinity;
    for (const k of legal) {
      const e = this.edges.get(k);
      if (!e || e.visits === 0) continue;
      const exploit = signFromRoot * (e.totalReward / e.visits);
      const explore = c * Math.sqrt(Math.log(e.availabilityCount) / e.visits);
      const score = exploit + explore;
      if (score > bestScore) {
        bestScore = score;
        best = k;
      }
    }
    return best;
  }

  untriedLegal(legal: readonly ActionKey[]): readonly ActionKey[] {
    const out: ActionKey[] = [];
    for (const k of legal) {
      const e = this.edges.get(k);
      if (!e || e.visits === 0) out.push(k);
    }
    return out;
  }

  bumpAvailability(legal: readonly ActionKey[]): void {
    for (const k of legal) {
      this.getOrCreateEdge(k).availabilityCount += 1;
    }
  }

  /** Action with highest visit count — the chosen move after search. */
  bestAction(): ActionKey | null {
    let best: ActionKey | null = null;
    let bestVisits = -1;
    for (const [k, e] of this.edges.entries()) {
      if (e.visits > bestVisits) {
        bestVisits = e.visits;
        best = k;
      }
    }
    return best;
  }
}
